import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import { MainForm, RequiredNotification } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import {
  useCancelHrmRequestMutation,
  useDeleteHrmRequestMutation,
  useGetHrmRequestFormQuery,
  useSubmitHrmRequestMutation,
} from '@Store/api/basicHrmApiSlice';
import { usePostPassportFormMutation } from '@Features/EmployeeSelfService/requests/PassportRequest/store/api/passportRequestApiSlice';
import { passportRequestActions } from '@Features/EmployeeSelfService/requests/PassportRequest/store/passportRequestSlice';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { getPassportTypeDictSelector } from '@Store/selectors/dictionarySelector';
import { NotificationContext } from '@Context/NotificationContext';
import { RequestError } from '@Types/api/form';
import { getTotalDays } from '@Utils/transformDateTime';
import { transformDictToOptions } from '@Utils/transformToOptions';
import { FooterActions } from '@Enum/index';
import { PassportStatusCodes } from '@Enum/api/PassportStatusCodes.enum';
import { getEditFormType } from '@Features/utils/getEditFormType';
import { validateForm } from '@Features/utils/validateForm';

import { PassportRequestFormSchema } from '../validationSchema';
import RequestDetailsSection from '../components/RequestDetailsSection';
import { initialValuesForm } from '../initialValues';
import { descriptionAlert } from '../descriptionAlert';
import { PassportRequestFormValues } from '../type';

const PassportRequestEditForm: React.FC = () => {
  const {
    editSuccessNotification,
    errorNotification,
    deleteSuccessNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { requestId } = useParams();
  const { data: editDataForm } = useGetHrmRequestFormQuery(requestId!, {
    refetchOnMountOrArgChange: true,
  });
  const requisitionInfo = useRequisitionInfoExistingReq(
    editDataForm,
    'Passport'
  );
  const passportTypes = useAppSelector(getPassportTypeDictSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const dispatch = useAppDispatch();
  const [postPassportForm] = usePostPassportFormMutation();
  const [deleteRequest] = useDeleteHrmRequestMutation();
  const [submitForm] = useSubmitHrmRequestMutation();
  const [cancelRequest] = useCancelHrmRequestMutation();

  const [showDeleteNotification, setShowDeleteNotification] = useState(false);
  const [disabledSaveForm, setDisableSaveForm] = useState(false);

  useEffect(
    () => () => {
      dispatch(passportRequestActions.setPassportRequest(null));
    },
    [requestId]
  );

  const handleSubmit = useCallback(
    (values: PassportRequestFormValues) => {
      const submitHandler = async (): Promise<void> => {
        const body = {
          requestId: editDataForm?.requestId,
          empNumber: editDataForm?.empNumber as string,
          leaveTypeID: Number(values.purpose),
          startDate: values.fromDate,
          endDate: values.returnDate,
          totDays: Number(values.totDays),
          remarks: values.remarks,
        };

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
          } = await postPassportForm(body).unwrap();

          dispatch(
            passportRequestActions.setPassportRequest({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          await submitForm(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitHandler();
    },
    [handleBackToListRequests, editDataForm]
  );

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    validationSchema: PassportRequestFormSchema,
    onSubmit: handleSubmit,
  });

  const handleSave = useCallback(async (): Promise<void> => {
    const isFormValid = await validateForm(PassportRequestFormSchema, formik);

    if (!isFormValid) return;

    const body = {
      empNumber: editDataForm?.empNumber as string,
      requestId: editDataForm?.requestId,
      leaveTypeID: Number(formik.values.purpose),
      startDate: formik.values.fromDate,
      endDate: formik.values.returnDate,
      totDays: Number(formik.values.totDays),
      remarks: formik.values.remarks,
    };

    setDisableSaveForm(true);

    try {
      const {
        requestId: reqId,
        createdDate,
        reqStatusId,
      } = await postPassportForm(body).unwrap();

      dispatch(
        passportRequestActions.setPassportRequest({
          createdDate,
          requestId: reqId,
          reqStatusId,
        })
      );

      editSuccessNotification(String(reqId));

      setDisableSaveForm(false);
    } catch (error) {
      const message = (error as RequestError)?.data;
      errorNotification(message);

      setDisableSaveForm(false);
    }
  }, [editDataForm, formik, PassportRequestFormSchema]);

  useEffect(() => {
    if (formik.values.fromDate && formik.values.returnDate) {
      const totalDays = getTotalDays(
        formik.values.returnDate,
        formik.values.fromDate
      );

      formik.setFieldValue('totDays', totalDays);
    } else {
      formik.setFieldValue('totDays', '');
    }
  }, [formik.values.fromDate, formik.values.returnDate]);

  useEffect(() => {
    if (editDataForm) {
      formik.setValues({
        ...editDataForm,
        purpose: String(editDataForm.leaveTypeID),
        fromDate: editDataForm.startDate,
        returnDate: editDataForm.endDate,
        totDays: String(editDataForm.totDays),
      });
    }
  }, [editDataForm]);

  const passportTypeOptions = useMemo(() => {
    return transformDictToOptions(passportTypes);
  }, [passportTypes]);

  const showDeleteRequestPopup = (): void => {
    setShowDeleteNotification(true);
  };

  const handleDeleteRequest = useCallback((): void => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    deleteRequest(reqId)
      .unwrap()
      .then(() => {
        deleteSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm, handleBackToListRequests]);

  const handleCancel = useCallback(() => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    cancelRequest(reqId)
      .unwrap()
      .then(() => {
        cancelSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [handleBackToListRequests, editDataForm]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.CANCEL) {
        handleCancel();
      }

      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSave();
      }

      if (action === FooterActions.DELETE_REQUEST) {
        showDeleteRequestPopup();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSave, handleCancel]
  );

  const isOpenStatus =
    !editDataForm || editDataForm.reqStatusId === PassportStatusCodes.OPEN;
  const formType = getEditFormType(isOpenStatus);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        descriptionAlert={descriptionAlert}
        disabledSaveForm={disabledSaveForm}
        formType={formType}
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            formApproval={false}
            passportTypeOptions={passportTypeOptions}
          />
        </FormItems>
      </MainForm>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${requestId}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={() => setShowDeleteNotification(false)}
        handleSuccess={handleDeleteRequest}
        isDeleteNotification
      />
    </Flex>
  );
};

export default PassportRequestEditForm;
